'use client';

import {
  SvgBenefitFill,
  SvgDonationFill,
  SvgHomeHeartFill,
  SvgMyFill,
  SvgStore,
} from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';

import { CustomLink } from '@/app/_components/CustomLink';
import {
  BREAKPOINTS,
  DONATION_ROUTES,
  LOCAL_MARKET_ROUTES,
  MODULE_ROUTES,
  ROUTE_TYPES,
  ROUTES,
  SELECTOR,
} from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { findRoute } from '@/utils';
import { gtmDatasets } from '@/utils/analytics/gtm';

const WELLO_NAVIGATION_ITEMS = [
  {
    name: '홈',
    route: ROUTES.HOME,
    href: ROUTES.HOME.pathname,
    icon: SvgHomeHeartFill,
    hasNotificationDot: false,
  },
  {
    name: '고향사랑기부',
    route: DONATION_ROUTES.HOME,
    href: DONATION_ROUTES.HOME.pathname,
    icon: SvgDonationFill,
    hasNotificationDot: true,
  },
  {
    name: '웰마켙',
    route: LOCAL_MARKET_ROUTES.HOME,
    href: LOCAL_MARKET_ROUTES.HOME.pathname,
    icon: SvgStore,
    hasNotificationDot: false,
  },
  {
    name: '혜택',
    route: ROUTES.COMMUNITY_CATEGORY,
    href: ROUTES.COMMUNITY.pathname,
    icon: SvgBenefitFill,
    hasNotificationDot: false,
  },
  {
    name: '마이페이지',
    route: ROUTES.MY,
    href: ROUTES.MY.pathname,
    icon: SvgMyFill,
    hasNotificationDot: false,
  },
];

export const GlobalNavigation = () => {
  const currentRouteType = useGlobalStore((state) => state.currentRouteType);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);
  const pathname = usePathname();

  const currentRoute = findRoute(pathname);

  const hideBottomNav = useGlobalStore((state) => state.hideBottomNav);

  const hasBottomNav = (() => {
    if (hideBottomNav) return false;

    //* /my 페이지는 진입 서비스의 BNB를 유지, 웰로일 때만 GNB 노출 (모바일만)
    if (pathname === ROUTES.MY.pathname) {
      if (
        currentRouteType === ROUTE_TYPES.DONATION ||
        currentRouteType === ROUTE_TYPES.LOCAL_MARKET
      ) {
        return false;
      }

      return isMobile && (currentRoute?.hasBottomNav ?? true);
    }

    switch (currentRouteType) {
      case ROUTE_TYPES.DONATION:
        return false;
      //* 웰로켓마켓은 별도의 BNB
      case ROUTE_TYPES.LOCAL_MARKET:
        return false;
      case ROUTE_TYPES.WELLO:
      default:
        return currentRoute?.hasBottomNav ?? true;
    }
  })();

  const { request: requestHaptic } = useHandleApp({
    type: MESSAGE_KEYWORD.HAPTIC,
  });

  const isLoading = useAuthStore((state) => state.isLogin === undefined);

  return (
    <nav
      aria-label="메인 내비게이션"
      className={cn(
        'relative flex w-full justify-center bg-Gray-white transition-colors duration-300',
        {
          'pb-[var(--safe-area-bottom,0px)]': hasBottomNav,
        },
      )}
      id={SELECTOR.GLOBAL_NAVIGATION}
    >
      {hasBottomNav ? (
        <ul
          className={cn(
            'flex justify-around transition-colors duration-300',
            'shadow-large h-70 w-full items-center border-t-1 border-Gray-50 px-22',
            {
              invisible: isLoading,
            },
          )}
        >
          {WELLO_NAVIGATION_ITEMS.map(
            ({ name, href, icon, hasNotificationDot }) => {
              const clickFnbDataset = gtmDatasets.clickFnb({
                menu_name: name,
              });

              const isActive =
                href === ROUTES.HOME.pathname
                  ? pathname === ROUTES.HOME.pathname ||
                    pathname === MODULE_ROUTES.KB_HOME.pathname
                  : pathname === href || pathname.startsWith(`${href}/`);

              const isCurrentPage = pathname === href;

              const Icon = icon;

              return (
                <li key={name} className="flex-1">
                  <CustomLink
                    aria-current={isCurrentPage ? 'page' : undefined}
                    {...clickFnbDataset}
                    className={cn(
                      'relative flex-col gap-5 whitespace-nowrap font-500 text-Gray-550 flex-center typo-caption1',
                      {
                        'text-Gray-900 font-700 cursor-default': isActive,
                      },
                    )}
                    href={href}
                    onTouchStart={() => {
                      if (!isCurrentPage) requestHaptic();
                    }}
                    onClick={(e) => {
                      if (isCurrentPage) return e.preventDefault();
                    }}
                  >
                    <div className="relative">
                      <Icon
                        className={cn('size-28 text-Gray-400', {
                          'animate-jump animate-duration-300 text-Gray-black':
                            isActive,
                        })}
                      />
                      {hasNotificationDot ? (
                        <>
                          <span className="sr-only">새 소식 있음</span>
                          <div
                            aria-hidden="true"
                            className="absolute -right-4 -top-2 size-6 rounded-pill bg-R-500"
                          />
                        </>
                      ) : null}
                    </div>
                    {name}
                  </CustomLink>
                </li>
              );
            },
          )}
        </ul>
      ) : null}
    </nav>
  );
};
